import React, { useEffect, useMemo, useState } from "react";
import CountUp from "react-countup";
import { useNavigate, Link } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";
import "swiper/css";

import api, { getRequest } from "../apis/ApiInstence";
import Owner from "../assests/images/aboutimage.png";

import Blog from "../components/Blog";

const About = () => {
  const navigate = useNavigate();
  const [achievements, setAchievements] = useState([]);
  const [achievementsLoading, setAchievementsLoading] = useState(true);
  const [achievementsError, setAchievementsError] = useState("");

  const assetBase = useMemo(() => {
    const base = String(api?.defaults?.baseURL || "").replace(/\/$/, "");
    return base.replace(/\/api\/v1$/, "");
  }, []);

  const resolveAchievementImage = (imagePath) => {
    const raw = String(imagePath || "").trim();
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;

    const normalized = raw.replace(/^\/+/, "");
    if (normalized.startsWith("achievements/")) return `${assetBase}/${normalized}`;
    return `${assetBase}/achievements/${normalized}`;
  };

  useEffect(() => {
    const controller = new AbortController();
    let mounted = true;

    const fetchAchievements = async () => {
      setAchievementsLoading(true);
      setAchievementsError("");

      try {
        const res = await getRequest("/achievements", { signal: controller.signal });
        const payload = res?.data;
        const list = Array.isArray(payload) ? payload : Array.isArray(payload?.data) ? payload.data : [];
        const normalized = list
          .filter(Boolean)
          .map((row) => ({
            id: row.id ?? row._id ?? `${row.heading || ""}-${Math.random()}`,
            title: row.heading ?? row.title ?? "",
            image: row.image ?? "",
          }))
          .filter((row) => row.title || row.image);

        if (!mounted) return;
        setAchievements(normalized);
      } catch (err) {
        if (!mounted) return;
        if (err?.name === "CanceledError") return;
        setAchievementsError("Achievements load nahi ho paaye. Please try again.");
        setAchievements([]);
      } finally {
        if (mounted) setAchievementsLoading(false);
      }
    };

    fetchAchievements();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, []);

  return (
    <div className="overflow-hidden bg-[#f8faff] ">

      {/* ================= BREADCRUMB ================= */}
      <div className="max-w-7xl mx-auto px-4 py-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 items-center gap-3">
          <div className="flex items-center gap-3 text-gray-500 text-sm">
            <button
              onClick={() => navigate("/")}
              className="w-9 h-9 flex items-center justify-center rounded-full border hover:bg-gray-100 transition"
            >
              ←
            </button>
            <span>
              <button onClick={() => navigate("/")} className="hover:underline">
                Home
              </button>{" "}
              / <span className="text-black font-medium">About Us</span>
            </span>
          </div>

          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-[#351F72] text-center">
            About Us
          </h1>

          <div className="hidden sm:block" />
        </div>
      </div>

      {/* ================= ABOUT HERO ================= */}
    <section className="relative lg:pb-40">
  <div className="absolute bottom-0 right-0 overflow-hidden">
    <img
      className="w-full h-auto origin-bottom-right transform scale-150 lg:w-auto lg:mx-auto lg:object-cover lg:scale-75"
      src="https://cdn.rareblocks.xyz/collection/clarity/images/hero/1/background-pattern.png"
      alt=""
    />
  </div>

  <div className="relative px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
    <div className="grid grid-cols-1 gap-y-4 lg:items-center lg:grid-cols-2 xl:grid-cols-2">

      {/* LEFT CONTENT */}
      <div className="text-center xl:col-span-1 lg:text-left md:px-16 lg:px-0 xl:pr-20">
        <h2 className="text-3xl sm:text-4xl font-extrabold text-[#351F72] tracking-tight">
          Who we are
        </h2>

        <p className="mt-2 text-lg text-gray-600 sm:mt-6 font-inter">
          PC Care is Jabalpur’s reliable computer service center, offering expert laptop repairs,
          refurbished laptops, and genuine accessories. We focus on fast service, honest pricing,
          and customer satisfaction for students, professionals, and businesses.
        </p>

              <div className="py-4">
                <Link
                  to="/contact"
                  className="group inline-flex items-center justify-center gap-3 
               bg-gradient-to-r from-[#351F72] to-[#4b2aa0] 
               text-white px-7 sm:px-10 py-3.5 
               rounded-full font-semibold 
               shadow-xl shadow-[#351F72]/30 
               hover:shadow-2xl hover:shadow-[#351F72]/50 
               hover:scale-105 active:scale-95 
               transition-all duration-300 text-sm sm:text-base"
                >

                  Contact Now
                </Link>
              </div>

        {/* RATING + TESTIMONIAL */}
        <div className="mt-8 sm:mt-16">
          <div className="flex items-center justify-center lg:justify-start">
            {[1, 2, 3, 4, 5].map((i) => (
              <svg
                key={i}
                className="w-5 h-5 text-[#FDB241]"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
            ))}
          </div>

          <blockquote className="mt-6">
            <p className="text-lg font-bold text-gray-900 font-pj">
              Best laptop repair service in Jabalpur!
            </p>
            <p className="mt-3 text-base leading-7 text-gray-600 font-inter">
              My laptop was running very slow and overheating. PC Care fixed it within one day.
              Honest pricing, genuine parts, and very professional service. Highly recommended!
            </p>
          </blockquote>

          <div className="flex items-center justify-center mt-3 lg:justify-start">
            <img
              className="flex-shrink-0 object-cover w-6 h-6 overflow-hidden rounded-full"
              src="https://cdn.rareblocks.xyz/collection/clarity/images/hero/1/avatar-female.png"
              alt=""
            />
            <p className="ml-2 text-base font-bold text-gray-900 font-pj">
              Rahul Verma, Jabalpur
            </p>
          </div>
        </div>
      </div>

      {/* RIGHT IMAGE */}
    <div className="xl:col-span-1 flex justify-center">
  <img
    className="w-64 sm:w-80 md:w-96 lg:w-[420px] h-auto rounded-xl shadow-lg"
    src={Owner}
    alt="PC Care Laptop Repair"
  />
</div>


    </div>
  </div>
</section>

      {/* ================= OUR GALLERY ================= */}
      <section className="py-10  lg:py-14">
        <div className="max-w-7xl px-4 mx-auto sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl  lg:text-4xl font-extrabold text-[#351F72] leading-tight">
              Our Achievements
            </h2>
          </div>

          <div className="mt-8 sm:mt-10">
            {achievementsLoading && (
              <div className="py-6 text-center text-gray-600 font-medium">
                Loading achievements...
              </div>
            )}
            {achievementsError && (
              <div className="py-6 text-center text-red-600 font-medium">
                {achievementsError}
              </div>
            )}
            {!achievementsLoading && !achievementsError && achievements.length === 0 && (
              <div className="py-6 text-center text-gray-600 font-medium">
                No achievements found.
              </div>
            )}

            {achievements.length > 0 && (
              <Swiper
                modules={[Autoplay]}
                loop
                grabCursor
                centeredSlides={false}
                autoplay={{
                  delay: 2200,
                  disableOnInteraction: false,
                  pauseOnMouseEnter: true,
                }}
                speed={700}
                spaceBetween={18}
                slidesPerView={1.15}
                breakpoints={{
                  480: { slidesPerView: 1.6, spaceBetween: 18 },
                  640: { slidesPerView: 2.1, spaceBetween: 20 },
                  768: { slidesPerView: 2.6, spaceBetween: 22 },
                  1024: { slidesPerView: 3.2, spaceBetween: 24 },
                }}
                className="px-4 sm:px-6 lg:px-8 py-6 sm:py-8"
              >
                {achievements.map((item) => {
                  const imageSrc = resolveAchievementImage(item.image);
                  return (
                    <SwiperSlide key={item.id}>
                      <div className="group h-full rounded-2xl bg-white border border-gray-100 shadow-md shadow-[#351F72]/5 overflow-hidden">
                        <div className="relative aspect-[16/10] overflow-hidden bg-gray-100">
                          {imageSrc ? (
                            <img
                              src={imageSrc}
                              alt={item.title || "Achievement"}
                              loading="lazy"
                              className="h-full w-full object-containt transition-transform duration-500 group-hover:scale-[1.06]"
                            />
                          ) : (
                            <div className="h-full w-full flex items-center justify-center text-sm text-gray-500">
                              No image
                            </div>
                          )}
                          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/35 via-black/0 to-black/0" />
                        </div>

                        <div className="p-4 sm:p-5">
                          <p className="text-base sm:text-lg font-semibold text-gray-900">
                            {item.title || "Achievement"}
                          </p>
                        </div>
                      </div>
                    </SwiperSlide>
                  );
                })}
              </Swiper>
            )}
          </div>
        </div>
      </section>
  <section className="py-10 lg:py-14 bg-white">
  <div className="max-w-7xl px-4 mx-auto sm:px-6 lg:px-8">
    
    {/* Heading */}
    <div className="max-w-2xl mx-auto text-center">
      <h2 className="text-2xl lg:text-5xl 
                     font-extrabold text-[#351F72] leading-tight">
        Our Story
      </h2>

      <p className="text-base sm:text-lg md:text-xl text-gray-600">
        Real experience. Real customers.
      </p>
    </div>

    {/* Stats Grid */}
  {/* Stats Grid */}
<div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 
                gap-10 sm:gap-12 mt-12 sm:mt-16 text-center">

  {/* Item 1 */}
  <div className="flex flex-col items-center">
    <h3 className="font-bold 
                   text-5xl sm:text-6xl md:text-6xl lg:text-7xl 
                   text-[#351F72]">
      <CountUp start={0} end={18} duration={2.5} />+
    </h3>
    <p className="mt-4 text-lg sm:text-xl font-medium text-gray-900">
      Trusted Experience Since 2008
    </p>
  </div>

  {/* Item 2 */}
  <div className="flex flex-col items-center">
    <h3 className="font-bold 
                   text-5xl sm:text-6xl md:text-6xl lg:text-7xl 
                   text-[#351F72]">
      <CountUp start={0} end={8500} duration={3} separator="," />+
    </h3>
    <p className="mt-4 text-lg sm:text-xl font-medium text-gray-900">
      Second Hand Laptops Sell
    </p>
  </div>

  {/* Item 3 */}
  <div className="flex flex-col items-center">
    <h3 className="font-bold 
                   text-5xl sm:text-6xl md:text-6xl lg:text-7xl 
                   text-[#351F72]">
      <CountUp start={0} end={98} duration={2.5} />%
    </h3>
    <p className="mt-4 text-lg sm:text-xl font-medium text-gray-900">
      Customer Satisfaction Rate
    </p>
  </div>

</div>
  </div>
</section>

      {/* ================= HOW IT WORKS (UNCHANGED) ================= */}
     <section className="py-10 sm:py-16 lg:py-24">
  <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
    <div className="max-w-2xl mx-auto text-center">
      <h2 className="text-3xl sm:text-4xl font-extrabold text-[#351F72] tracking-tight mb-5">
        How does it work?
      </h2>
    </div>

    <div className="relative mt-12 lg:mt-20">
      <div className="absolute inset-x-0 hidden xl:px-44 top-2 md:block md:px-20 lg:px-28">
        <img
          className="w-full"
          src="https://cdn.rareblocks.xyz/collection/celebration/images/steps/2/curved-dotted-line.svg"
          alt=""
        />
      </div>

      <div className="relative grid grid-cols-1 text-center gap-y-12 md:grid-cols-3 gap-x-12">
        {/* STEP 1 */}
        <div>
          <div className="flex items-center justify-center w-16 h-16 mx-auto bg-white border-2 border-gray-200 rounded-full shadow">
            <span className="text-xl font-semibold text-gray-700">1</span>
          </div>
          <h3 className="mt-6 text-xl font-semibold leading-tight text-black md:mt-10">
            Free Login & Booking
          </h3>
          <p className="mt-4 text-base text-gray-600">
            Create a free account or contact PC Care and book your product
            repair or service easily with complete assistance.
          </p>
        </div>

        {/* STEP 2 */}
        <div>
          <div className="flex items-center justify-center w-16 h-16 mx-auto bg-white border-2 border-gray-200 rounded-full shadow">
            <span className="text-xl font-semibold text-gray-700">2</span>
          </div>
          <h3 className="mt-6 text-xl font-semibold leading-tight text-black md:mt-10">
            Testing & Secure Payment
          </h3>
          <p className="mt-4 text-base text-gray-600">
            Our experts diagnose and test your device, share the issue clearly,
            and proceed after your approval with safe and transparent payment.
          </p>
        </div>

        {/* STEP 3 */}
        <div>
          <div className="flex items-center justify-center w-16 h-16 mx-auto bg-white border-2 border-gray-200 rounded-full shadow">
            <span className="text-xl font-semibold text-gray-700">3</span>
          </div>
          <h3 className="mt-6 text-xl font-semibold leading-tight text-black md:mt-10">
            Repair & Delivery
          </h3>
          <p className="mt-4 text-base text-gray-600">
            After successful repair and final quality check, your product is
            safely delivered back to you on time.
          </p>
        </div>
      </div>
    </div>
  </div>
</section>
<Blog />

      {/* ================= COUNTERS (FIXED) ================= */}
   

    </div>
  );
};

export default About;
